<script setup lang="ts">
import { Filter, FilterX, RotateCcw, Settings2 } from '@lucide/vue'
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, ZhTooltip } from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import ListSearchField from '@/components/ListSearchField.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import type { ListState } from '@/composables/useListState'
import { useUiMode } from '@/composables/useUiMode'
import { getNestedValue } from '@/lib/utils'

export type DataTableColumnType = 'text' | 'select' | 'date' | 'number'

export type DataTableColumn = {
    key: string
    label: string
    sort?: string
    type?: DataTableColumnType
    filterable?: boolean
    options?: Array<{ value: string; label: string }>
    defaultVisible?: boolean
    /** Hidden while the portal is in Standard mode, shown in Advanced. */
    advancedOnly?: boolean
    class?: string
}

type Row = Record<string, unknown>

const props = withDefaults(
    defineProps<{
        columns: DataTableColumn[]
        rows: Row[]
        total: number
        page: number
        limit: number
        pages: number
        loading?: boolean
        search?: string
        sort?: string
        order?: 'ASC' | 'DESC'
        filters?: Record<string, string>
        /** Search box and filter panel. Off for a table the server hands out unfiltered, page by page. */
        searchable?: boolean
        /** Keep filters available when a parent supplies the search field. */
        hideSearchInput?: boolean
        storageKey?: string
        rowKey?: string | ((row: Row) => string)
        /** Extra classes for one row (e.g. highlight a live row). */
        rowClass?: (row: Row) => string | undefined
        /** Slide new rows in and glide rows to a new position when their order changes. */
        animateRows?: boolean
        /**
         * Stack each row into a card under the shared mobile breakpoint, instead of a <table> that
         * pushes its trailing columns (usually the actions) off a phone screen. Opt-in: every table
         * that does not set it renders exactly as before at every width.
         */
        stackedOnMobile?: boolean
        /**
         * Columns an older build took out of the saved choice on its own, not because the person unchecked them
         * (#1958: Standard stripped Kind from Tasks). A list saved in that older format gets them back once, and is
         * saved again in the current format, so a later deliberate uncheck sticks.
         */
        restoreLegacyKeys?: string[]
        /**
         * The page's saved list choices (#633, `useListState`): with it, the toolbar offers Clear filters whenever the
         * list differs from its defaults, even narrowed to nothing.
         */
        listState?: ListState
    }>(),
    {
        loading: false,
        searchable: true,
        hideSearchInput: false,
        search: '',
        sort: '',
        order: 'DESC',
        filters: () => ({}),
        rowKey: 'id',
        rowClass: undefined,
        animateRows: false,
        stackedOnMobile: false,
        restoreLegacyKeys: () => [],
        listState: undefined,
    },
)

const emit = defineEmits<{
    'update:page': [number]
    'update:limit': [number]
    'update:search': [string]
    'update:sort': [string]
    'update:order': ['ASC' | 'DESC']
    'update:filters': [Record<string, string>]
    'row-click': [Row]
}>()

const filtersOpen = ref(false)
const columnsOpen = ref(false)
const searchBox = ref(props.search)
// A search the page sets itself (Clear filters, a restored choice) shows in the box too.
watch(
    () => props.search,
    value => {
        searchBox.value = value
    },
)
/** The list is narrowed by a saved choice: Clear filters puts every one back to the page's defaults. */
const narrowed = computed(() => props.listState !== undefined && !props.listState.isDefault.value)

function clearFilters(): void {
    props.listState?.clear()
    emit('update:page', 1)
}

/*
 * The two toolbar resets sit side by side, so each says what it changes (#633): Clear filters puts back the list's
 * saved choices and leaves the columns; Reset also puts back the columns.
 */
const CLEAR_FILTERS_HINT = 'Clears your saved search, filters and sort for this list. The columns you chose stay.'
const RESET_HINT = 'Shows the default columns again, and clears the search, filters and sort.'
const isMobile = useIsMobile()
/** A row is a card, not a table row, once an opted-in table is read on a phone. */
const stacked = computed(() => props.stackedOnMobile && isMobile.value)

/*
 * Two things decide which columns show, and they are kept apart (#1958):
 * - what the person chose to see, across every column: a saved setting, written only when they check or uncheck a
 *   column or press Reset;
 * - what the Standard/Advanced mode hides: Advanced-only columns, left out on screen while in Standard and back the
 *   moment the mode is Advanced again. The mode never rewrites the choice.
 */
const { mode } = useUiMode()

/** The columns this mode offers, in the header, the rows, the cards and the Columns and Filters panels. */
const offeredColumns = computed(() => props.columns.filter(column => !column.advancedOnly || mode.value === 'advanced'))

function defaultKeysOf(columns: DataTableColumn[]): string[] {
    return columns.filter(column => column.defaultVisible !== false).map(column => column.key)
}

const filterableColumns = computed(() =>
    offeredColumns.value.filter(column => {
        if (column.filterable === false) {
            return false
        }
        const type = column.type ?? 'text'
        return ['text', 'select', 'date', 'number'].includes(type)
    }),
)

/**
 * The saved choice. Version 2 marks a list this build wrote: an older build saved a bare array of keys, rewritten on
 * every load with whatever the mode had removed, so a bare array may still miss a column the person never unchecked.
 */
const SAVED_VERSION = 2

type SavedColumns = { version: typeof SAVED_VERSION; visible: string[] }

function savedKey(): string | null {
    return props.storageKey ? `os:table:${props.storageKey}:cols` : null
}

/** The browser's saved settings, or null where a private window or the test runner offers none. */
function storage(): Storage | null {
    try {
        const store = globalThis.localStorage
        return store && typeof store.getItem === 'function' ? store : null
    } catch {
        return null
    }
}

function persist(keys: string[]): void {
    const key = savedKey()
    if (!key) {
        return
    }
    const value: SavedColumns = { version: SAVED_VERSION, visible: keys }
    try {
        storage()?.setItem(key, JSON.stringify(value))
    } catch {
        // Storage can be unavailable (private mode, blocked cookies): column visibility then
        // simply does not persist, which must not break the table.
    }
}

function keysIn(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((key): key is string => typeof key === 'string') : []
}

function isCurrent(value: unknown): value is SavedColumns {
    return typeof value === 'object' && value !== null && (value as { version?: unknown }).version === SAVED_VERSION
}

/** Puts `key` back after the last column defined before it that the list holds, so it returns to its own place. */
function insertInColumnOrder(keys: string[], key: string): string[] {
    const order = props.columns.map(column => column.key)
    const before = new Set(order.slice(0, Math.max(order.indexOf(key), 0)))
    const at = keys.reduce((index, current, position) => (before.has(current) ? position + 1 : index), 0)
    return [...keys.slice(0, at), key, ...keys.slice(at)]
}

/**
 * A list in the older format gets back the columns the older build removed on its own, once: it is saved again in
 * the current format, which is never repaired, so a later deliberate uncheck is kept. A list missing nothing is left
 * exactly as it was saved.
 */
function repairLegacy(keys: string[]): string[] {
    const missing = props.restoreLegacyKeys.filter(key => !keys.includes(key))
    if (missing.length === 0) {
        return keys
    }
    const repaired = missing.reduce(insertInColumnOrder, keys)
    persist(repaired)
    return repaired
}

/** The person's saved choice, or null when there is none and the table shows its defaults. Reading never saves. */
function loadChoice(): string[] | null {
    const key = savedKey()
    if (!key) {
        return null
    }
    try {
        const raw = storage()?.getItem(key)
        if (!raw) {
            return null
        }
        const parsed: unknown = JSON.parse(raw)
        if (Array.isArray(parsed)) {
            const legacy = keysIn(parsed)
            return legacy.length > 0 ? repairLegacy(legacy) : null
        }
        const keys = isCurrent(parsed) ? keysIn(parsed.visible) : []
        return keys.length > 0 ? keys : null
    } catch {
        return null
    }
}

const chosenKeys = ref<string[] | null>(loadChoice())

/** What the person chose to see across every column, Advanced-only ones included; the defaults until they choose. */
const choice = computed(() => chosenKeys.value ?? defaultKeysOf(props.columns))

/**
 * The columns on screen: the choice, less what this mode hides. A choice that leaves this mode nothing shows the
 * mode's own defaults instead, on screen only, so the table always has a column and the choice is never touched.
 */
const visibleColumns = computed(() => {
    const offered = new Map(offeredColumns.value.map(column => [column.key, column]))
    const picked = [...new Set(choice.value)]
        .map(key => offered.get(key))
        .filter((column): column is DataTableColumn => Boolean(column))
    if (picked.length > 0) {
        return picked
    }
    const fallback = offeredColumns.value.filter(column => column.defaultVisible !== false)
    return fallback.length > 0 ? fallback : offeredColumns.value.slice(0, 1)
})

const visibleKeys = computed(() => visibleColumns.value.map(column => column.key))

/** The person's own change: the only time, with Reset, that the choice is saved. */
function choose(keys: string[]): void {
    chosenKeys.value = keys
    persist(keys)
}

const from = computed(() => (props.total === 0 ? 0 : (props.page - 1) * props.limit + 1))
const to = computed(() => Math.min(props.page * props.limit, props.total))

function idOf(row: Row): string {
    if (typeof props.rowKey === 'function') {
        return props.rowKey(row)
    }
    return String(row[props.rowKey] ?? '')
}

function cell(row: Row, key: string): unknown {
    return getNestedValue(row, key)
}

function toggleSort(column: DataTableColumn): void {
    if (!column.sort) {
        return
    }
    if (props.sort === column.sort) {
        emit('update:order', props.order === 'ASC' ? 'DESC' : 'ASC')
        return
    }
    emit('update:sort', column.sort)
    emit('update:order', 'ASC')
}

function setFilter(key: string, value: string): void {
    const next = { ...props.filters }
    if (value) {
        next[key] = value
    } else {
        delete next[key]
    }
    emit('update:filters', next)
    emit('update:page', 1)
}

function submitSearch(): void {
    emit('update:search', searchBox.value)
    emit('update:page', 1)
}

/**
 * Checks or unchecks one column and leaves every other one, Advanced-only ones included, as the person chose. The
 * last column this mode shows stays on. What is on screen counts as chosen, even where Standard shows its defaults in
 * place of a choice that left it none.
 */
function toggleColumn(key: string): void {
    const shown = visibleKeys.value
    const isShown = shown.includes(key)
    if (isShown && shown.length === 1) {
        return
    }
    const base = [...new Set([...choice.value, ...shown])]
    choose(isShown ? base.filter(item => item !== key) : [...base, key])
}

/** The sizes the pager offers, as the kit Select's string values; `setLimit` hands consumers the number. */
const PAGE_SIZES = ['10', '25', '50', '100'] as const

function setLimit(value: unknown): void {
    emit('update:limit', Number(value))
    emit('update:page', 1)
}

/**
 * Restores the defaults of every column, not only the ones this mode shows, so Advanced keeps its own, and puts the
 * list back to its defaults. A page that keeps its list choices (#633) is put back through its own `clear`: to the
 * page's own default sort and order, with the saved entry removed, so the list is not narrowed after Reset and Clear
 * filters goes. Without list state the table only knows its columns, so it asks for the first sortable one, ascending.
 */
function reset(): void {
    searchBox.value = ''
    choose(defaultKeysOf(props.columns))
    if (props.listState) {
        props.listState.clear()
        emit('update:page', 1)
        return
    }
    emit('update:search', '')
    emit('update:filters', {})
    emit('update:page', 1)
    emit('update:sort', props.columns.find(column => column.sort)?.sort ?? '')
    emit('update:order', 'ASC')
}

const INPUT_TYPE_MAP: Record<DataTableColumnType, string> = {
    text: 'text',
    select: 'text',
    date: 'date',
    number: 'number',
}

function filterInputType(column: DataTableColumn): string {
    return INPUT_TYPE_MAP[column.type ?? 'text']
}
</script>

<template>
	<div class="space-y-3">
		<div class="flex flex-wrap items-end justify-between gap-2">
			<div class="flex flex-wrap items-end gap-2">
				<form v-if="searchable && !hideSearchInput" class="flex flex-wrap items-end gap-2" @submit.prevent="submitSearch">
					<label class="text-sm">
						<span class="text-muted-foreground">Search</span>
						<ListSearchField v-model="searchBox" class="mt-1 block min-w-48" placeholder="Search" />
					</label>
					<Button type="submit" size="sm" variant="outline">Search</Button>
				</form>
				<Button
					v-if="searchable && filterableColumns.length"
					type="button"
					size="sm"
					:variant="filtersOpen ? 'default' : 'outline'"
					@click="filtersOpen = !filtersOpen"
				>
					<Filter class="size-4" />
					Filters
				</Button>
				<Button type="button" size="sm" variant="outline" @click="columnsOpen = !columnsOpen">
					<Settings2 class="size-4" />
					Columns
				</Button>
			</div>
			<div class="flex flex-wrap items-end gap-2">
				<ZhTooltip v-if="narrowed" :text="CLEAR_FILTERS_HINT">
					<Button type="button" size="sm" variant="outline" @click="clearFilters">
						<FilterX class="size-4" />
						Clear filters
					</Button>
				</ZhTooltip>
				<ZhTooltip :text="RESET_HINT">
					<Button type="button" size="sm" variant="ghost" @click="reset">
						<RotateCcw class="size-4" />
						Reset
					</Button>
				</ZhTooltip>
			</div>
		</div>

		<div v-if="searchable && filtersOpen && filterableColumns.length" class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
			<label v-for="column in filterableColumns" :key="column.key" class="text-sm">
				<span class="text-muted-foreground">{{ column.label }}</span>
				<select
					v-if="column.type === 'select'"
					class="border-input bg-background mt-1 block w-full rounded-md border px-2 py-1"
					:value="filters[column.key] ?? ''"
					@change="setFilter(column.key, ($event.target as HTMLSelectElement).value)"
				>
					<option value="">All</option>
					<option v-for="option in column.options ?? []" :key="option.value" :value="option.value">
						{{ option.label }}
					</option>
				</select>
				<input
					v-else
					class="border-input bg-background mt-1 block w-full rounded-md border px-2 py-1"
					:type="filterInputType(column)"
					:value="filters[column.key] ?? ''"
					:placeholder="`Filter by ${column.label.toLowerCase()}`"
					@change="setFilter(column.key, ($event.target as HTMLInputElement).value)"
				/>
			</label>
		</div>

		<div v-if="columnsOpen" class="bg-background max-w-sm space-y-2 rounded-md border p-3 text-sm">
			<p class="font-medium">Visible columns</p>
			<label v-for="column in offeredColumns" :key="column.key" class="flex items-center gap-2">
				<input
					type="checkbox"
					class="accent-foreground"
					:checked="visibleKeys.includes(column.key)"
					:disabled="visibleKeys.length === 1 && visibleKeys.includes(column.key)"
					@change="toggleColumn(column.key)"
				/>
				<span>{{ column.label }}</span>
			</label>
		</div>

		<ul v-if="stacked" class="space-y-3">
			<li v-if="loading && !rows.length" class="text-muted-foreground bg-card border p-4 text-sm">Loading…</li>
			<li v-else-if="!rows.length" class="text-muted-foreground bg-card border p-4 text-sm">No rows.</li>
			<template v-else>
				<li
					v-for="row in rows"
					:key="idOf(row)"
					:data-row-id="idOf(row)"
					class="space-y-3 bg-card border p-4 text-sm"
					:class="rowClass?.(row)"
					@click="emit('row-click', row)"
				>
					<div v-for="column in visibleColumns" :key="column.key" class="flex flex-col gap-1">
						<span class="text-muted-foreground text-xs">{{ column.label }}</span>
						<slot :name="`cell-${column.key}`" :row="row" :value="cell(row, column.key)">
							<span>{{ cell(row, column.key) ?? '—' }}</span>
						</slot>
					</div>
				</li>
			</template>
		</ul>
		<div v-else class="overflow-x-auto bg-card border">
			<table class="w-full text-left text-sm">
				<thead class="bg-muted/40">
					<tr>
						<th
							v-for="column in visibleColumns"
							:key="column.key"
							class="px-3 py-2 font-medium"
							:class="column.class"
						>
							<button
								v-if="column.sort"
								type="button"
								class="hover:underline"
								@click="toggleSort(column)"
							>
								{{ column.label }}
								<span v-if="sort === column.sort">{{ order === 'ASC' ? ' ↑' : ' ↓' }}</span>
							</button>
							<span v-else>{{ column.label }}</span>
						</th>
					</tr>
				</thead>
				<!-- Rows stay on screen while a reload is in flight, so a refresh never blanks the table. The body is a
				     real <tbody> with the TransitionGroup inside it rendering no element of its own, so the rows sit in
				     the same <tbody> wherever the group is replaced, as test utils replace it with a stub. -->
				<tbody>
					<TransitionGroup
						:enter-active-class="animateRows ? 'transition duration-500 ease-out' : ''"
						:enter-from-class="animateRows ? '-translate-y-2 opacity-0' : ''"
						:move-class="animateRows ? 'transition-transform duration-500 ease-in-out' : ''"
					>
						<tr v-if="loading && !rows.length" key="__loading">
							<td class="text-muted-foreground px-3 py-4" :colspan="Math.max(visibleColumns.length, 1)">
								Loading…
							</td>
						</tr>
						<tr v-else-if="!rows.length" key="__empty">
							<td class="text-muted-foreground px-3 py-4" :colspan="Math.max(visibleColumns.length, 1)">
								No rows.
							</td>
						</tr>
						<template v-else>
							<tr
								v-for="row in rows"
								:key="idOf(row)"
								:data-row-id="idOf(row)"
								class="hover:bg-muted/30 cursor-pointer border-t"
								:class="rowClass?.(row)"
								@click="emit('row-click', row)"
							>
								<td
									v-for="column in visibleColumns"
									:key="column.key"
									class="px-3 py-2 align-top"
									:class="column.class"
								>
									<slot :name="`cell-${column.key}`" :row="row" :value="cell(row, column.key)">
										{{ cell(row, column.key) ?? '—' }}
									</slot>
								</td>
							</tr>
						</template>
					</TransitionGroup>
				</tbody>
			</table>
		</div>

		<div class="flex flex-wrap items-center justify-between gap-3 text-sm">
			<p class="text-muted-foreground">
				{{ total === 0 ? 'Showing 0 of 0' : `Showing ${from}–${to} of ${total}` }}
			</p>
			<div class="flex flex-wrap items-center gap-3">
				<!-- The kit Select draws the page-size control; the trigger is named, since a label names no combobox. -->
				<div class="text-muted-foreground flex items-center gap-2">
					Show
					<Select :model-value="String(limit)" data-testid="page-size" @update:model-value="setLimit">
						<SelectTrigger size="sm" aria-label="Rows per page">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<p class="text-muted-foreground">Page {{ page }} of {{ pages }}</p>
				<div class="flex gap-2">
					<Button
						type="button"
						size="sm"
						variant="outline"
						:disabled="page <= 1 || loading"
						@click="emit('update:page', page - 1)"
					>
						Previous
					</Button>
					<Button
						type="button"
						size="sm"
						variant="outline"
						:disabled="page >= pages || loading"
						@click="emit('update:page', page + 1)"
					>
						Next
					</Button>
				</div>
			</div>
		</div>
	</div>
</template>
